/**
 * Floor-editor de salón — geometría PURA del plano de mesas.
 *
 * El plano es una grilla fija de celdas (`FLOOR_GRID_COLS` × `FLOOR_GRID_ROWS`):
 * cada mesa colocada guarda su celda en `dining_table.pos_x/pos_y` (enteros,
 * NULL = sin colocar). Trabajar en celdas —y no en píxeles— hace el layout
 * independiente del tamaño de pantalla de cada terminal y determinista de
 * testear; el componente traduce celda ↔ píxel con el tamaño real del
 * contenedor.
 */

export const FLOOR_GRID_COLS = 12;
export const FLOOR_GRID_ROWS = 8;

export interface FloorCell {
  x: number;
  y: number;
}

/** ¿Tiene la mesa una posición asignada en el plano? */
export function isPlaced(table: { pos_x: number | null; pos_y: number | null }): boolean {
  return table.pos_x !== null && table.pos_y !== null;
}

/** Restringe una celda a los límites de la grilla. */
export function clampCell(
  x: number,
  y: number,
  cols = FLOOR_GRID_COLS,
  rows = FLOOR_GRID_ROWS,
): FloorCell {
  const cx = Math.min(Math.max(Math.trunc(x), 0), cols - 1);
  const cy = Math.min(Math.max(Math.trunc(y), 0), rows - 1);
  return { x: cx, y: cy };
}

/**
 * Traduce un punto en píxeles (offset dentro del contenedor) a su celda,
 * ya restringida a la grilla. `width`/`height` = tamaño del contenedor.
 */
export function pointToCell(
  px: number,
  py: number,
  width: number,
  height: number,
  cols = FLOOR_GRID_COLS,
  rows = FLOOR_GRID_ROWS,
): FloorCell {
  if (width <= 0 || height <= 0) {
    return { x: 0, y: 0 };
  }
  return clampCell(Math.floor((px / width) * cols), Math.floor((py / height) * rows), cols, rows);
}

export function cellKey(x: number, y: number): string {
  return `${x},${y}`;
}

/**
 * Posición por defecto para la mesa `index`-ésima SIN colocar: recorre la
 * grilla en orden de lectura saltando celdas ocupadas. Si el plano está lleno,
 * reutiliza la última celda (caso degenerado: más mesas que celdas).
 */
export function autoPlaceCell(
  index: number,
  taken: ReadonlySet<string>,
  cols = FLOOR_GRID_COLS,
  rows = FLOOR_GRID_ROWS,
): FloorCell {
  let seen = 0;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (taken.has(cellKey(x, y))) {
        continue;
      }
      if (seen === index) {
        return { x, y };
      }
      seen++;
    }
  }
  return { x: cols - 1, y: rows - 1 };
}

/**
 * Resuelve la celda visible de CADA mesa: las colocadas usan su celda
 * persistida (clamped por robustez); las no colocadas se auto-reparten en las
 * celdas libres en orden estable. Puro: mismo input → mismo plano.
 */
export function resolveFloorLayout(
  tables: readonly { id: string; pos_x: number | null; pos_y: number | null }[],
  cols = FLOOR_GRID_COLS,
  rows = FLOOR_GRID_ROWS,
): Map<string, FloorCell> {
  const out = new Map<string, FloorCell>();
  const taken = new Set<string>();
  for (const t of tables) {
    if (isPlaced(t)) {
      const cell = clampCell(t.pos_x ?? 0, t.pos_y ?? 0, cols, rows);
      out.set(t.id, cell);
      taken.add(cellKey(cell.x, cell.y));
    }
  }
  let i = 0;
  for (const t of tables) {
    if (!isPlaced(t)) {
      const cell = autoPlaceCell(i, taken, cols, rows);
      out.set(t.id, cell);
      i++;
    }
  }
  return out;
}
